"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { keepPreviousData, type InfiniteData, type UseInfiniteQueryResult, useInfiniteQuery } from "@tanstack/react-query";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { SortDirection } from "@/entities/ISort";
import type { ApiXTdhGrantToken } from "@/generated/models/ApiXTdhGrantToken";
import type { ApiXTdhGrantTokensPage } from "@/generated/models/ApiXTdhGrantTokensPage";
import { commonApiFetch } from "@/services/api/common-api";
const DEFAULT_PAGE_SIZE = 500;
const MAX_PAGE_SIZE = 2000;
interface UseTdhGrantTokensQueryParams {
  readonly grantId: string;
  readonly pageSize?: number | undefined;
  readonly enabled?: boolean | undefined;
}
type UseTdhGrantTokensQueryResult = UseInfiniteQueryResult<InfiniteData<ApiXTdhGrantTokensPage>, Error> & {
  readonly tokens: ApiXTdhGrantToken[];
  readonly totalCount: number;
};
export function useTdhGrantTokensQuery(t0) {
  const $ = _c(18);
  const {
    grantId,
    pageSize: t1,
    enabled: t2
  } = t0;
  const pageSize = t1 === undefined ? DEFAULT_PAGE_SIZE : t1;
  const enabled = t2 === undefined ? true : t2;
  let t3;
  if ($[0] !== pageSize) {
    t3 = normalizePageSize(pageSize);
    $[0] = pageSize;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const normalizedPageSize = t3;
  const isEnabled = Boolean(grantId) && enabled;
  let t4;
  if ($[2] !== grantId || $[3] !== normalizedPageSize) {
    t4 = [QueryKey.TDH_GRANT_TOKENS, grantId, normalizedPageSize];
    $[2] = grantId;
    $[3] = normalizedPageSize;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== grantId || $[6] !== normalizedPageSize) {
    t5 = async t6 => {
      const {
        pageParam: t7
      } = t6;
      const pageParam = t7 === undefined ? 1 : t7;
      return await commonApiFetch({
        endpoint: `xtdh/grants/${grantId}/tokens`,
        params: {
          page: pageParam.toString(),
          page_size: normalizedPageSize.toString(),
          sort: "token",
          sort_direction: SortDirection.ASC
        }
      });
    };
    $[5] = grantId;
    $[6] = normalizedPageSize;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== isEnabled || $[9] !== t4 || $[10] !== t5) {
    t6 = {
      queryKey: t4,
      enabled: isEnabled,
      initialPageParam: 1,
      staleTime: 30000,
      placeholderData: keepPreviousData,
      queryFn: t5,
      getNextPageParam: _temp
    };
    $[8] = isEnabled;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const query = useInfiniteQuery(t6);
  let t7;
  if ($[12] !== query.data?.pages) {
    t7 = query.data?.pages.flatMap(_temp2) ?? [];
    $[12] = query.data?.pages;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const tokens = t7;
  const totalCount = query.data?.pages?.[0]?.count ?? 0;
  let t8;
  if ($[14] !== query || $[15] !== tokens || $[16] !== totalCount) {
    t8 = {
      ...query,
      tokens,
      totalCount
    };
    $[14] = query;
    $[15] = tokens;
    $[16] = totalCount;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
function _temp2(page) {
  return page.data;
}
function _temp(lastPage) {
  return lastPage.next ? lastPage.page + 1 : undefined;
}
function normalizePageSize(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return DEFAULT_PAGE_SIZE;
  }
  return Math.min(Math.floor(value), MAX_PAGE_SIZE);
}